import { cn } from '@sohanemon/utils';
import { useMediaQuery } from '@sohanemon/utils/hooks';
import { useScrollTransform } from '@sohanscript/lib/hooks/scroll-transform';
import * as m from 'motion/react-m';
import {
	type ComponentProps,
	type ComponentRef,
	type CSSProperties,
	useRef,
} from 'react';
import { ReactSVG as SVG } from 'react-svg';
import { MotionProvider } from './motion';

type BgCircleProps = ComponentProps<typeof m.div> & { parallax?: boolean };

export function BgCircle({
	className,
	parallax = true,
	...props
}: BgCircleProps) {
	const isSm = useMediaQuery('sm');
	const target = useRef<ComponentRef<typeof m.div>>(null);
	const transform = useScrollTransform({
		target,
		offset: ['start center', 'end start'],
		output: { scale: [1, 3], opacity: [1, 0.2] },
	});

	return (
		<MotionProvider>
			<m.div
				ref={target}
				{...props}
				className={cn(
					'relative flex h-(--circle-size,192px) w-(--circle-size,192px) items-center justify-center',
					className,
				)}
				animate={{
					scale: isSm ? [1.5, 0.2, 1] : [1.5, 0.2, 0.75],
				}}
				initial={{
					scale: 2,
				}}
				transition={{
					duration: 1,
				}}
			>
				<SVG
					style={
						{
							'--svg-size': 'calc(var(--circle-size, 192px) * 80 / 192)',
						} as CSSProperties
					}
					className="absolute animate-ping text-primary [&_svg]:h-(--svg-size) [&_svg]:w-(--svg-size)"
					src="/hexagon.svg"
				/>
				<SVG
					style={
						{
							'--svg-size': 'var(--circle-size, 192px)',
						} as CSSProperties
					}
					className="text-primary opacity-30 [&_svg]:h-(--svg-size) [&_svg]:w-(--svg-size)"
					src="/hexagon.svg"
				/>
				<m.div
					suppressHydrationWarning
					className="absolute text-primary"
					style={
						{
							...(isSm && parallax ? transform.output : {}),
							'--svg-size': 'calc(var(--circle-size, 192px) * 0.7)',
						} as CSSProperties
					}
				>
					<SVG
						className="[&_svg]:h-(--svg-size) [&_svg]:w-(--svg-size)"
						src="/hexagon-spaced.svg"
					/>
				</m.div>
			</m.div>
		</MotionProvider>
	);
}
